import { useEffect, useMemo } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";

export const FINISHES = {
  obsidian: { puck: "#1c1c1e", seam: "#303033", holder: "#121214" },
  ceramic: { puck: "#ededea", seam: "#d6d6d1", holder: "#f1f1ed" },
};

const puckProfile = (R = 1.585, H = 0.4, r = 0.3, steps = 10) => {
  const pts = [new THREE.Vector2(0, -H)];
  for (let i = 0; i <= steps; i++) {
    const a = -Math.PI / 2 + (i / steps) * (Math.PI / 2);
    pts.push(new THREE.Vector2(R - r + Math.cos(a) * r, -H + r + Math.sin(a) * r));
  }
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * (Math.PI / 2);
    pts.push(new THREE.Vector2(R - r + Math.cos(a) * r, H - r + Math.sin(a) * r));
  }
  pts.push(new THREE.Vector2(0, H));
  return pts;
};

export const usePuckGeometry = () => useMemo(() => new THREE.LatheGeometry(puckProfile(), 96), []);

export const useHolderGeometries = () =>
  useMemo(() => {
    const box = new RoundedBoxGeometry(4.6, 0.4, 3.5, 6, 0.45);
    const shape = new THREE.Shape();
    shape.absarc(0, 0, 1.74, 0, Math.PI * 2, false);
    const hole = new THREE.Path();
    hole.absarc(0, 0, 1.62, 0, Math.PI * 2, true);
    shape.holes.push(hole);
    const rim = new THREE.ExtrudeGeometry(shape, {
      depth: 0.3,
      bevelEnabled: true,
      bevelThickness: 0.04,
      bevelSize: 0.04,
      bevelSegments: 4,
      curveSegments: 72,
    });
    rim.rotateX(-Math.PI / 2);
    return { box, rim };
  }, []);

export const useSheetGeometry = () => useMemo(() => new RoundedBoxGeometry(4.0, 0.07, 3.0, 4, 0.45), []);

/** PMREM room environment for soft studio reflections. */
export const Environment = ({ intensity = 0.85 }) => {
  const { gl, scene } = useThree();
  useEffect(() => {
    const pmrem = new THREE.PMREMGenerator(gl);
    const tex = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = tex;
    scene.environmentIntensity = intensity;
    return () => {
      scene.environment = null;
      tex.dispose();
      pmrem.dispose();
    };
  }, [gl, scene, intensity]);
  return null;
};

/** Puck + seam + LED. */
export const Puck = ({ colors, geometry }) => (
  <>
    <mesh geometry={geometry} castShadow receiveShadow>
      <meshPhysicalMaterial color={colors.puck} roughness={0.48} metalness={0.02} clearcoat={0.25} clearcoatRoughness={0.6} />
    </mesh>
    <mesh rotation={[Math.PI / 2, 0, 0]}>
      <torusGeometry args={[1.57, 0.014, 12, 128]} />
      <meshStandardMaterial color={colors.seam} roughness={0.5} />
    </mesh>
    <mesh position={[0.95, 0.4, 0]} userData={{ led: true }}>
      <sphereGeometry args={[0.045, 24, 24]} />
      <meshStandardMaterial color="#FF6B00" emissive="#FF6B00" emissiveIntensity={1.6} />
    </mesh>
  </>
);

/** Magnetic protective case (4.6 × 3.5 × 0.7 cm): slab + pocket rim. */
export const Holder = ({ colors, geometries }) => (
  <>
    <mesh geometry={geometries.box} castShadow receiveShadow>
      <meshStandardMaterial color={colors.holder} roughness={0.9} metalness={0} />
    </mesh>
    <mesh geometry={geometries.rim} position={[0, 0.2, 0]} castShadow receiveShadow>
      <meshStandardMaterial color={colors.holder} roughness={0.9} metalness={0} />
    </mesh>
  </>
);
